export const DEFAULT_LOCATION_COLOR = '#D1D5DB';
export const HEX_COLOR_RE = /^#[0-9A-Fa-f]{6}$/;
const DARK_LOCATION_TEXT_COLOR = '#111827';
const LIGHT_LOCATION_TEXT_COLOR = '#FFFFFF';

export const PRESET_LOCATION_COLORS = [
  '#4CAF50', // Green
  '#E91E63', // Pink
  '#F44336', // Red
  '#FFEB3B', // Yellow
  '#FF9800', // Orange
  '#03A9F4', // Blue
  '#9C27B0', // Purple
  '#795548', // Brown
];

export function isValidHexColor(value: string | null | undefined): boolean {
  if (!value) return false;
  return HEX_COLOR_RE.test(value.trim());
}

export function normalizeLocationColor(value: string | null | undefined): string {
  if (!isValidHexColor(value)) return DEFAULT_LOCATION_COLOR;
  return value!.trim().toUpperCase();
}

function getRelativeLuminance(hexColor: string): number {
  const color = normalizeLocationColor(hexColor).slice(1);
  const rgb = [0, 2, 4].map((start) => {
    const channel = parseInt(color.slice(start, start + 2), 16) / 255;
    return channel <= 0.03928
      ? channel / 12.92
      : ((channel + 0.055) / 1.055) ** 2.4;
  });

  return 0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2];
}

function getContrastRatio(backgroundColor: string, textColor: string): number {
  const backgroundLuminance = getRelativeLuminance(backgroundColor);
  const textLuminance = getRelativeLuminance(textColor);
  const [lighter, darker] = backgroundLuminance > textLuminance
    ? [backgroundLuminance, textLuminance]
    : [textLuminance, backgroundLuminance];

  return (lighter + 0.05) / (darker + 0.05);
}

export function getLocationTextColor(value: string | null | undefined): string {
  const backgroundColor = normalizeLocationColor(value);
  const darkContrast = getContrastRatio(backgroundColor, DARK_LOCATION_TEXT_COLOR);
  const lightContrast = getContrastRatio(backgroundColor, LIGHT_LOCATION_TEXT_COLOR);

  return darkContrast >= lightContrast
    ? DARK_LOCATION_TEXT_COLOR
    : LIGHT_LOCATION_TEXT_COLOR;
}

/**
 * Returns inline style object for location color badges.
 * Uses 15% alpha background with matching text color,
 * falling back to default text color for the default gray.
 */
export function getLocationBadgeStyles(colorCode: string | null | undefined): {
  backgroundColor: string;
  color: string | undefined;
} {
  const bg = normalizeLocationColor(colorCode);
  return {
    backgroundColor: `${bg}25`,
    color: bg === DEFAULT_LOCATION_COLOR ? undefined : bg,
  };
}
